import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { RefreshCwIcon } from 'lucide-react';

import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { usePropertyAppraisal } from '@modules/_shared/hooks/useAppraiseProperty';
import type { AppraisalResult } from '@modules/reports/cma-reports/step-listing-appraise/StepHedonisticValuation';
import { useLocale } from '@shared/hooks/locale';

type AppraisalSectionProps = {
  propertyId: string | undefined;
  latestAppraisalId: string | undefined;
  appraisalsLength: number;
  appraisalLoading: boolean;
  appraisable?: AppraisalResult;
  onAppraise: () => void;
};

const AppraisalSection = ({
  propertyId,
  latestAppraisalId,
  appraisalsLength,
  appraisalLoading,
  appraisable,
  onAppraise,
}: AppraisalSectionProps) => {
  const { t } = useLocale();
  const { appraiseProperty, appraisalData, mutationLoading } =
    usePropertyAppraisal(latestAppraisalId);

  const handleAppraiseProperty = async () => {
    if (propertyId) {
      await appraiseProperty(propertyId);
    }
    onAppraise();
  };

  return (
    <Box
      sx={{
        gap: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
        }}
      >
        {t('Latest appraisal')}
      </Typography>
      {appraisalsLength > 0 && appraisable?.result === true ? (
        <>
          {appraisalData != null && (
            <Typography variant="subtitle1">
              <TimeAgo dateString={appraisalData?.updated_at} />
            </Typography>
          )}
          <Box>
            <Button
              loading={mutationLoading}
              variant="contained"
              loadingPosition="start"
              startIcon={<RefreshCwIcon />}
              onClick={handleAppraiseProperty}
            >
              {t('Appraise again')}
            </Button>
          </Box>
        </>
      ) : appraisalLoading ? (
        <Stack
          sx={{
            gap: 1,
          }}
        >
          <Skeleton variant="rounded" height={24} width={150} />
          <Skeleton variant="rounded" height={24} width={140} />
          <Skeleton variant="rounded" height={36} width={200} />
        </Stack>
      ) : (
        appraisable?.result === true && (
          <>
            <Typography variant="subtitle1">
              {t('This property is not appraised yet')}
            </Typography>
            <Box>
              <Button
                loading={mutationLoading}
                loadingPosition="start"
                startIcon={<RefreshCwIcon />}
                onClick={handleAppraiseProperty}
              >
                {t('Start appraising')}
              </Button>
            </Box>
          </>
        )
      )}
    </Box>
  );
};

export default AppraisalSection;
